import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  AutoComplete,
  Button,
  Card,
  Collapse,
  Form,
  Input,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons'
import {
  getAdminPollDefinition,
  updateAdminOption,
  updateAdminQuestion,
} from '../../../features/poll/api-admin'
import { Poll, PollQuestion } from '../../../features/poll/types'
import { getMenuLinksCatalog } from '../../../menu/linksCatalog'
import B2BLayout from '../../../layouts/b2b'
import { HStack } from '../../../ui/Stack'

const { Title, Text } = Typography

const SYSTEM_KEY_OPTIONS = [
  { value: 'brand_name', label: 'brand_name — название бренда' },
  { value: 'company_departments', label: 'company_departments — подразделения' },
  { value: 'requisite.name', label: 'requisite.name — название организации' },
  { value: 'requisite.inn', label: 'requisite.inn — ИНН' },
  { value: 'requisite.kpp', label: 'requisite.kpp — КПП' },
  { value: 'requisite.ogrn', label: 'requisite.ogrn — ОГРН' },
  { value: 'requisite.address', label: 'requisite.address — юридический адрес' },
  { value: 'requisite.director', label: 'requisite.director — генеральный директор' },
  { value: 'requisite.bank_name', label: 'requisite.bank_name — банк' },
  { value: 'requisite.bik', label: 'requisite.bik — БИК' },
  { value: 'requisite.invoice_curr', label: 'requisite.invoice_curr — расчётный счёт' },
  { value: 'requisite.invoice_corr', label: 'requisite.invoice_corr — корр. счёт' },
]

const MODULE_OPTIONS = [
  { value: 'sales', label: 'Продажи' },
  { value: 'logistics', label: 'Логистика' },
  { value: 'hr', label: 'HR' },
  { value: 'claims', label: 'Рекламации' },
  { value: 'documents', label: 'Документы' },
  { value: 'montage', label: 'Монтажи' },
  { value: 'measurements', label: 'Замеры' },
]

const QUESTION_TYPE_LABELS: Record<string, string> = {
  checkbox: 'Чекбоксы',
  radio: 'Радио',
  select: 'Список',
  text: 'Текст',
  textarea: 'Текст',
  number: 'Число',
  switch: 'Переключатель',
}

const CATALOG_OPTIONS = (() => {
  const byKey = new Map<string, { value: string; label: string; group: string }>()
  for (const item of getMenuLinksCatalog()) {
    if (byKey.has(item.key)) continue
    byKey.set(item.key, {
      value: item.key,
      label: `${item.title} (${item.key})`,
      group: item.group,
    })
  }
  const groups = new Map<string, { value: string; label: string }[]>()
  for (const option of byKey.values()) {
    const list = groups.get(option.group) || []
    list.push({ value: option.value, label: option.label })
    groups.set(option.group, list)
  }
  return Array.from(groups.entries()).map(([label, options]) => ({ label, options }))
})()

type QuestionDraft = {
  system_key: string
  locked: boolean
}

type OptionDraft = {
  code: string
  locked: boolean
  required: boolean
  modules: string[]
  catalogKeys: string[]
}

function emptyQuestionDraft(): QuestionDraft {
  return { system_key: '', locked: false }
}

function emptyOptionDraft(): OptionDraft {
  return { code: '', locked: false, required: false, modules: [], catalogKeys: [] }
}

function buildQuestionConfig(existing: Record<string, any> | undefined, draft: QuestionDraft) {
  const config = { ...(existing || {}) }
  const systemKey = draft.system_key.trim()
  if (systemKey) config.system_key = systemKey
  else delete config.system_key
  config.locked = Boolean(draft.locked)
  return config
}

function buildOptionConfig(existing: Record<string, any> | undefined, draft: OptionDraft) {
  const config = { ...(existing || {}) }
  const code = draft.code.trim()
  if (code) config.code = code
  else delete config.code
  config.locked = Boolean(draft.locked)
  config.required = Boolean(draft.required)
  config.modules = draft.modules
  config.catalogKeys = draft.catalogKeys
  return config
}

export default function PollBindingsPage() {
  const { quizId } = useParams<{ quizId: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [poll, setPoll] = useState<Poll | null>(null)
  const [questionDrafts, setQuestionDrafts] = useState<Record<string, QuestionDraft>>({})
  const [optionDrafts, setOptionDrafts] = useState<Record<string, OptionDraft>>({})

  const load = useCallback(async () => {
    if (!quizId) return
    setLoading(true)
    try {
      const data = await getAdminPollDefinition(quizId)
      setPoll(data)
      const nextQuestions: Record<string, QuestionDraft> = {}
      const nextOptions: Record<string, OptionDraft> = {}
      for (const question of data?.questions || []) {
        nextQuestions[question.id] = {
          system_key: question.config?.system_key || '',
          locked: Boolean(question.config?.locked),
        }
        for (const option of question.options || []) {
          nextOptions[option.id] = {
            code: option.config?.code || '',
            locked: Boolean(option.config?.locked),
            required: Boolean(option.config?.required),
            modules: option.config?.modules || [],
            catalogKeys: option.config?.catalogKeys || [],
          }
        }
      }
      setQuestionDrafts(nextQuestions)
      setOptionDrafts(nextOptions)
    } catch (e: any) {
      message.error(e?.message || 'Не удалось загрузить опрос')
    } finally {
      setLoading(false)
    }
  }, [quizId])

  useEffect(() => {
    load()
  }, [load])

  const patchQuestion = (questionId: string, patch: Partial<QuestionDraft>) => {
    setQuestionDrafts(prev => ({
      ...prev,
      [questionId]: { ...(prev[questionId] || emptyQuestionDraft()), ...patch },
    }))
  }

  const patchOption = (optionId: string, patch: Partial<OptionDraft>) => {
    setOptionDrafts(prev => ({
      ...prev,
      [optionId]: { ...(prev[optionId] || emptyOptionDraft()), ...patch },
    }))
  }

  const save = async () => {
    if (!quizId || !poll) return
    setSaving(true)
    try {
      const tasks: Promise<unknown>[] = []
      for (const question of poll.questions) {
        const draft = questionDrafts[question.id] || emptyQuestionDraft()
        tasks.push(
          updateAdminQuestion(quizId, question.id, {
            config: buildQuestionConfig(question.config, draft),
          }),
        )
        for (const option of question.options || []) {
          const optionDraft = optionDrafts[option.id] || emptyOptionDraft()
          tasks.push(
            updateAdminOption(quizId, question.id, option.id, {
              config: buildOptionConfig(option.config, optionDraft),
            }),
          )
        }
      }
      await Promise.all(tasks)
      message.success('Привязки сохранены в опрос')
      await load()
    } catch (e: any) {
      message.error(e?.message || 'Не удалось сохранить привязки')
    } finally {
      setSaving(false)
    }
  }

  const renderQuestionFields = (question: PollQuestion) => {
    const draft = questionDrafts[question.id] || emptyQuestionDraft()
    return (
      <Form layout="vertical" requiredMark={false}>
        <HStack gap="16" align="start" wrap>
          <Form.Item label="Системный ключ" style={{ marginBottom: 0, flex: 1, minWidth: 280 }}>
            <AutoComplete
              value={draft.system_key}
              options={SYSTEM_KEY_OPTIONS}
              onChange={value => patchQuestion(question.id, { system_key: value })}
              placeholder="Не привязан — только аналитика"
              filterOption={(input, option) =>
                String(option?.label || option?.value || '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              allowClear
            />
          </Form.Item>
          <Form.Item label="Системный вопрос" style={{ marginBottom: 0 }}>
            <Switch
              checked={draft.locked}
              onChange={checked => patchQuestion(question.id, { locked: checked })}
              checkedChildren="Нельзя удалить"
              unCheckedChildren="Можно удалить"
            />
          </Form.Item>
        </HStack>
      </Form>
    )
  }

  const renderOptionsTable = (question: PollQuestion) => {
    const options = question.options || []
    if (!options.length) return null

    return (
      <Table
        size="small"
        pagination={false}
        rowKey="id"
        style={{ marginTop: 16 }}
        scroll={{ x: 960 }}
        dataSource={options}
        columns={[
          {
            title: 'Вариант',
            dataIndex: 'label',
            width: 220,
            render: (label: string) => <Text>{label}</Text>,
          },
          {
            title: 'Код',
            width: 160,
            render: (_: unknown, option) => {
              const draft = optionDrafts[option.id] || emptyOptionDraft()
              return (
                <Input
                  value={draft.code}
                  placeholder="has_sales"
                  onChange={e => patchOption(option.id, { code: e.target.value })}
                />
              )
            },
          },
          {
            title: 'Обязательный',
            width: 120,
            align: 'center',
            render: (_: unknown, option) => {
              const draft = optionDrafts[option.id] || emptyOptionDraft()
              return (
                <Switch
                  size="small"
                  checked={draft.required}
                  onChange={checked => patchOption(option.id, { required: checked })}
                />
              )
            },
          },
          {
            title: 'Системный',
            width: 110,
            align: 'center',
            render: (_: unknown, option) => {
              const draft = optionDrafts[option.id] || emptyOptionDraft()
              return (
                <Switch
                  size="small"
                  checked={draft.locked}
                  onChange={checked => patchOption(option.id, { locked: checked })}
                />
              )
            },
          },
          {
            title: 'Модули',
            width: 220,
            render: (_: unknown, option) => {
              const draft = optionDrafts[option.id] || emptyOptionDraft()
              return (
                <Select
                  mode="multiple"
                  allowClear
                  value={draft.modules}
                  options={MODULE_OPTIONS}
                  placeholder="Нет"
                  maxTagCount="responsive"
                  style={{ width: '100%' }}
                  onChange={modules => patchOption(option.id, { modules })}
                />
              )
            },
          },
          {
            title: 'Пункты меню',
            render: (_: unknown, option) => {
              const draft = optionDrafts[option.id] || emptyOptionDraft()
              return (
                <Select
                  mode="multiple"
                  allowClear
                  value={draft.catalogKeys}
                  options={CATALOG_OPTIONS}
                  placeholder="Нет"
                  maxTagCount="responsive"
                  optionFilterProp="label"
                  style={{ width: '100%' }}
                  onChange={catalogKeys => patchOption(option.id, { catalogKeys })}
                />
              )
            },
          },
        ]}
      />
    )
  }

  const steps = [...(poll?.steps || [])].sort((a, b) => a.order_index - b.order_index)
  const questionsByStep = (stepId: string) =>
    (poll?.questions || [])
      .filter(question => question.step_id === stepId)
      .sort((a, b) => a.order_index - b.order_index)

  return (
    <B2BLayout
      pageTitle="Системная привязка"
      breadcrumbs={[
        { title: 'Опросы', href: '/admin/dealers/polls' },
        { title: poll?.title || 'Системная привязка' },
      ]}
    >
      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        <HStack justify="between" align="center" wrap>
          <HStack justify="start" align="center" gap="16">
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/dealers/polls')}>
              К опросам
            </Button>
            <div>
              <Title level={2} style={{ margin: 0 }}>
                Системная привязка стартового опроса
              </Title>
            </div>
          </HStack>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={save}
            loading={saving}
            disabled={!poll}
          >
            Сохранить
          </Button>
        </HStack>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spin />
          </div>
        ) : (
          <Collapse
            defaultActiveKey={steps.map(step => step.id)}
            items={steps.map(step => {
              const questions = questionsByStep(step.id)
              return {
                key: step.id,
                label: (
                  <Space>
                    <Text strong>{step.title}</Text>
                    <Tag>{questions.length}</Tag>
                  </Space>
                ),
                children: (
                  <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    {step.description && <Text type="secondary">{step.description}</Text>}
                    {questions.map(question => (
                      <Card
                        key={question.id}
                        size="small"
                        title={
                          <Space wrap>
                            <Text strong>{question.title}</Text>
                            <Tag>{QUESTION_TYPE_LABELS[question.type] || question.type}</Tag>
                            {question.is_required && <Tag color="red">Обязательный</Tag>}
                          </Space>
                        }
                      >
                        {question.subtitle && (
                          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                            {question.subtitle}
                          </Text>
                        )}
                        {renderQuestionFields(question)}
                        {renderOptionsTable(question)}
                      </Card>
                    ))}
                  </Space>
                ),
              }
            })}
          />
        )}
      </Space>
    </B2BLayout>
  )
}
